/* eslint-disable no-magic-numbers */
import { icon, node } from "~/util/dom";
import RibbonCont from "./cont";
import { getStyleValues } from "./utils";
import { PARKING_LEFT, THRESHOLD } from "./const";
import { TDebugColors, TOnClickTypes } from "./interfaces";

interface IRibbon {
	parking?: number;
	threshold?: number;
	debug?: boolean;
	onClick?: (data: TOnClickTypes) => void;
}

export default class Ribbon {
	_dom: {[key: string]: HTMLElement};

	_contents: {
		news: HTMLDivElement;
		photos: HTMLDivElement;
		reviews: HTMLDivElement;
		pois: HTMLDivElement;
	};

	_step: number;

	_steps: Array<number>;

	_activeContent: RibbonCont;

	_options: IRibbon;

	_removeProm: Promise<any>;

	constructor({
		parking = PARKING_LEFT,
		threshold = THRESHOLD,
		debug = false,
		onClick = () => {},
	}: IRibbon) {
		this._options = {
			parking,
			threshold,
			debug,
			onClick,
		};
		this._dom = {};
		this._activeContent = null;
		this._step = 0;
		this._steps = [];
		this._removeProm = null;

		this._build();
	}

	get container() {
		return this._dom.container;
	}

	get content() {
		return this._activeContent;
	}

	set debugTitle(title: string) {
		if (this._options.debug) {
			this._dom.container.title = title;
		}
	}

	set loader(state: boolean) {
		this._dom.container.classList[state ? "add" : "remove"]("loading");
	}

	debugColor(color?: TDebugColors) {
		if (color && this._options.debug) {
			this._dom.container.setAttribute("data-color", color);
		} else {
			this._dom.container.removeAttribute("data-color");
		}
	}

	async setContent(content: RibbonCont) {
		if (!content) {
			if (this._activeContent) {
				this._activeContent.destroy();
			}

			this._activeContent = null;

			return;
		}

		await this._removeActiveContent(content);

		this._step = 0;
		this._calculateSteps();
		this._syncLeftRightButtons();
	}

	setShowButton(state: boolean, text: string) {
		this._dom.btnShow.innerHTML = "";
		this._dom.btnShow.append(icon(state ? "visibilityOn" : "visibilityOff"));
		this._dom.btnShow.append(node("span", { className: "ribbon__btnShowText", textContent: text }));
	}

	syncPort() {
		this._calculateSteps();
		this._syncLeftRightButtons();
	}

	contentParking() {
		if (this._activeContent) {
			this._activeContent.left = 0;
		}

		this._step = 0;
		this._calculateSteps();
		this._syncLeftRightButtons();
	}

	_syncLeftRightButtons() {
		this._dom.btnLeft.classList[this._step === 0 ? "add" : "remove"]("hide");
		this._dom.btnRight.classList[this._step === this._steps.length - 1 ? "add" : "remove"]("hide");
	}

	_build() {
		const ribbon = node("div", { className: "ribbon" });
		const ribbonTopLeftBorder = node("div", { className: "ribbon__topLeftBorder" });
		const ribbonTopRightBorder = node("div", { className: "ribbon__topRightBorder" });
		const ribbonTopBtnCover = node("div", { className: "ribbon__topBtnCover" });
		const ribbonScroller = node("div", { className: "ribbon__scroller" });
		const svgBtnRibbonDown = node("button", { type: "button", className: "ribbon__btnDown" });
		const btnShow = node("button", { type: "button", className: "ribbon__btnShow" });

		svgBtnRibbonDown.append(icon("ribbon-button-down"));
		svgBtnRibbonDown.addEventListener("click", () => {
			this._options.onClick({ name: "down" });
		});

		const btnLeft = node("button", { className: "ribbon__leftBtn ribbon__leftRightBtns", type: "button" });
		const btnRight = node("button", { className: "ribbon__rightBtn ribbon__leftRightBtns", type: "button" });

		btnLeft.classList.add("hide");
		btnRight.classList.add("hide");
		btnLeft.append(icon("ribbon-button-left"));
		btnRight.append(icon("ribbon-button-right"));
		btnLeft.addEventListener("click", () => {
			this._setDirection(-1);
		});
		btnRight.addEventListener("click", () => {
			this._setDirection(1);
		});
		btnShow.addEventListener("click", () => {
			this._options.onClick({ name: "visibility" });
		});
		ribbon.append(svgBtnRibbonDown, ribbonScroller, ribbonTopLeftBorder, ribbonTopRightBorder, ribbonTopBtnCover, btnLeft, btnRight, btnShow);

		if (this._options.debug) {
			const parking = node("div", { className: "ribbon__threshold" });
			const threshold = node("div", { className: "ribbon__threshold" });

			parking.style.left = `${this._options.parking}px`;
			threshold.style.right = `${this._options.threshold}px`;
			ribbon.append(parking, threshold);
		}

		this._dom.container = ribbon;
		this._dom.ribbonScroller = ribbonScroller;
		this._dom.btnShow = btnShow;
		this._dom.btnLeft = btnLeft;
		this._dom.btnRight = btnRight;
		this._dom.svgBtnRibbonDown = svgBtnRibbonDown;
	}

	_calculateSteps() {
		if (!this._activeContent) {
			return;
		}

		const [paddingLeft, paddingRight] = getStyleValues(this._dom.container, ["paddingLeft", "paddingRight"]);
		// sirka contentu
		const width = this._dom.ribbonScroller.offsetWidth;
		// parking
		const left = this._options.parking - paddingLeft;
		const right = this._options.threshold - paddingRight;

		this._steps = this._activeContent.getSteps({ width, left, right });
	}

	_setDirection(direction: number) {
		const newStep = Math.min(Math.max(this._step + direction, 0), this._steps.length - 1);

		if (newStep !== this._step) {
			this._step = newStep;
			this._activeContent.left = this._steps[this._step];
			this._options.onClick({
				name: direction > 0 ? "right" : "left",
				step: this._step,
			});
			this._syncLeftRightButtons();
		}
	}

	async _removeActiveContent(content: RibbonCont) {
		if (this._removeProm) {
			await this._removeProm;
			this._removeProm = null;
		}

		if (this._activeContent) {
			const prom = content.animShow();

			this._dom.ribbonScroller.append(content.container);
			this._removeProm = Promise.all([
				this._activeContent.destroy(),
				prom,
			]);

			await this._removeProm;

			this._activeContent = content;
		} else {
			this._activeContent = content;
			this._dom.ribbonScroller.append(content.container);
		}

		content.onClick(data => {
			this._options.onClick(data);
		});
		this._removeProm = null;
	}
}
